// Modules tab: short exercises the guide may offer once exploration is done.
// Every module must say when it must NOT be used (neverWhen), and can map to
// an existing guided-exercise protocol instead of being improvised.

import type { FollowUp, TalkTrackContent, TalkTrackModule } from "../../types/talkTrack";
import { FOLLOW_UPS } from "../../types/talkTrack";
import { ListField, SelectField, TextField } from "./fields";
import { nextUnitId } from "./format";
import { UnitCard } from "./UnitCard";
import { useUnitContext } from "./unitContext";

export function ModulesEditor({
  topicId,
  content,
  moduleTypes,
  protocols,
  onChange,
}: {
  topicId: string;
  content: TalkTrackContent;
  moduleTypes: string[];
  protocols: string[];
  onChange: (next: TalkTrackContent) => void;
}) {
  const { editable } = useUnitContext();
  const setModules = (modules: TalkTrackModule[]) => onChange({ ...content, modules });
  const patch = (i: number, p: Partial<TalkTrackModule>) =>
    setModules(content.modules.map((m, j) => (j === i ? { ...m, ...p } : m)));

  function add() {
    const id = nextUnitId(content, `${topicId}.module.`, content.modules.length + 1);
    setModules([
      ...content.modules,
      {
        id, type: moduleTypes[0] || "cognitive_reframe", title: "", approach: "", requires: [],
        neverWhen: [], steps: [], choiceVoice: "", options: [], followUp: "none", successSignal: "",
        mapsToProtocol: null,
      },
    ]);
  }

  return (
    <>
      {content.modules.length === 0 && (
        <div className="state-box">No modules yet{editable ? " — add one below." : "."}</div>
      )}
      {content.modules.map((m, i) => (
        <UnitCard key={m.id} unitId={m.id} kind="Module" title={m.title}
          onRemove={() => setModules(content.modules.filter((_, j) => j !== i))}>
          <div className="row-3">
            <TextField label="Title" editable={editable} value={m.title} onChange={(title) => patch(i, { title })} />
            <SelectField label="Type" editable={editable} value={m.type} options={moduleTypes}
              onChange={(type) => patch(i, { type })} />
            <TextField label="Approach" editable={editable} value={m.approach} placeholder="Evidence-based"
              onChange={(approach) => patch(i, { approach })} />
          </div>
          <div className="row">
            <ListField label="Requires (before offering)" editable={editable} value={m.requires} rows={2}
              onChange={(requires) => patch(i, { requires })} />
            <ListField label="Never when (blocks the module)" editable={editable} value={m.neverWhen} rows={2}
              onChange={(neverWhen) => patch(i, { neverWhen })} />
          </div>
          <ListField label="Steps (spoken across one or two turns)" editable={editable} value={m.steps} rows={4}
            onChange={(steps) => patch(i, { steps })} />
          <ListField label="Choices (offered to the user as written)" editable={editable} value={m.options} rows={2}
            onChange={(options) => patch(i, { options })} />
          <TextField label="AI spoken choice (reference only — the guide doesn't use it)" editable={editable}
            value={m.choiceVoice} onChange={(choiceVoice) => patch(i, { choiceVoice })} />
          <div className="row-3">
            <SelectField label="Follow-up" editable={editable} value={m.followUp} options={FOLLOW_UPS}
              onChange={(followUp) => patch(i, { followUp: followUp as FollowUp })} />
            <SelectField label="Maps to protocol" editable={editable} value={m.mapsToProtocol || ""}
              options={protocols} emptyLabel="none"
              onChange={(p) => patch(i, { mapsToProtocol: p || null })} />
            <TextField label="Success signal" editable={editable} value={m.successSignal}
              onChange={(successSignal) => patch(i, { successSignal })} />
          </div>
        </UnitCard>
      ))}
      {editable && <button className="btn" onClick={add}>+ Module</button>}
    </>
  );
}
